import type {
  CoachAssignableServiceOption,
  CoachProfile,
  CoachServiceAssignmentOperatorResult,
  CoachServiceAssignmentPreview,
  CoachServiceAssignmentPreviewRequest,
  TableSnapshot,
} from "@billiard/contracts";
import { isCoachServiceAssignmentPreview } from "@billiard/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Clock3, UserPlus, UsersRound } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  assignCoachService,
  type CoachServiceAssignmentCommand,
  type CoachServiceAssignmentRecoveryCommand,
  coachServiceAssignmentFailureMessage,
  coachServiceAssignmentLegacyResultRequiresStart,
  coachServiceAssignmentOutcomeIsUnknown,
  getCoachServiceAssignmentByRequest,
} from "./api";
import {
  beginCoachServiceAssignmentDispatch,
  type CoachServiceAssignmentRecovery,
  type CoachServiceAssignmentRecoveryView,
  claimCoachServiceAssignmentRecovery,
  clearCoachServiceAssignmentRecovery,
  coachServiceAssignmentContext,
  finishCoachServiceAssignmentDispatch,
  markCoachServiceAssignmentOutcomeUnknown,
  readCoachServiceAssignmentRecovery,
  reconcileCoachServiceAssignmentAuthority,
} from "./coachServiceAssignmentRecovery";

export type CoachServiceOptionsState = "DISABLED" | "LOADING" | "ERROR" | "EMPTY" | "READY";

export type CoachCustomerRequestAssignmentQuery =
  | { status: "NONE" }
  | { status: "INVALID" }
  | { status: "VALID"; customerRequestEventId: string; sessionId: string };

export function coachCustomerRequestAssignmentQuery(
  search: string,
): CoachCustomerRequestAssignmentQuery {
  const params = new URLSearchParams(search);
  const eventIds = params.getAll("customerRequestEventId");
  if (eventIds.length === 0) return { status: "NONE" };
  const sessionIds = params.getAll("sessionId");
  const hasUnexpectedKey = [...params.keys()].some(
    (key) => key !== "customerRequestEventId" && key !== "sessionId",
  );
  if (eventIds.length !== 1 || sessionIds.length !== 1 || hasUnexpectedKey) {
    return { status: "INVALID" };
  }
  const customerRequestEventId = eventIds[0] ?? "";
  const sessionId = sessionIds[0] ?? "";
  if (
    customerRequestEventId.length < 8 ||
    customerRequestEventId.length > 128 ||
    sessionId.length < 1 ||
    sessionId.length > 128
  ) {
    return { status: "INVALID" };
  }
  return { status: "VALID", customerRequestEventId, sessionId };
}

function nextAssignmentKey(): string {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `coach-service-assignment:${random}`;
}

function money(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

function dateTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

type CoachServiceAssignmentPreviewState =
  | "IDLE"
  | "LOADING"
  | "READY"
  | "EXPIRED"
  | "ERROR"
  | "UNAVAILABLE";

function assignmentPreviewMatches(
  preview: CoachServiceAssignmentPreview,
  request: CoachServiceAssignmentPreviewRequest,
  now: number,
): boolean {
  return (
    isCoachServiceAssignmentPreview(preview) &&
    preview.sessionId === request.sessionId &&
    preview.coachId === request.expectedCoachId &&
    preview.assignmentType === request.assignmentType &&
    preview.serviceRuleId === request.serviceRuleId &&
    preview.priceFen === request.expectedPriceFen &&
    preview.pricingToken === request.pricingToken &&
    Date.parse(preview.expiresAt) > now
  );
}

export function CoachServiceAssignmentPanel({
  operator,
  activeTables,
  tableReadFailed,
  assignableCoaches,
  rotationEnabled,
  rotationCoachId,
  rotationAssignmentEmptyReason,
  requestedAssignmentEmptyReason,
  serviceOptions,
  serviceOptionsState,
  managerReasonRequired = false,
  blocked,
  preview: providedPreview = null,
  onPreview,
  onRecoveryViewChange,
  onCompleted,
  onConfirmed,
}: {
  operator: { tenantId: string; storeId: string; id: string };
  activeTables: readonly TableSnapshot[];
  tableReadFailed: boolean;
  assignableCoaches: readonly Pick<
    CoachProfile,
    "id" | "coachNumber" | "displayName" | "levelId"
  >[];
  rotationEnabled: boolean;
  rotationCoachId: string | undefined;
  rotationAssignmentEmptyReason: string | null;
  requestedAssignmentEmptyReason: string | null;
  serviceOptions: readonly CoachAssignableServiceOption[];
  serviceOptionsState: CoachServiceOptionsState;
  managerReasonRequired?: boolean;
  blocked: boolean;
  preview?: CoachServiceAssignmentPreview | null;
  onPreview?: (
    request: CoachServiceAssignmentPreviewRequest,
  ) => Promise<CoachServiceAssignmentPreview>;
  onRecoveryViewChange?: (view: CoachServiceAssignmentRecoveryView) => void;
  onCompleted: () => Promise<void> | void;
  onConfirmed?: (result: CoachServiceAssignmentOperatorResult, sessionId: string) => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const customerRequestQuery = useMemo(
    () => coachCustomerRequestAssignmentQuery(location.search),
    [location.search],
  );
  const context = useMemo(() => coachServiceAssignmentContext(operator), [operator]);
  const [assignmentType, setAssignmentType] = useState<"ROTATION" | "REQUESTED">(
    rotationEnabled ? "ROTATION" : "REQUESTED",
  );
  const [sessionId, setSessionId] = useState("");
  const [requestedCoachId, setRequestedCoachId] = useState("");
  const [serviceRuleId, setServiceRuleId] = useState("");
  const [managerReason, setManagerReason] = useState("");
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<{ kind: "INFO" | "ERROR"; text: string } | null>(null);
  const [completed, setCompleted] = useState<CoachServiceAssignmentOperatorResult | null>(null);
  const [completedCustomerRequest, setCompletedCustomerRequest] = useState(false);
  const [resolvedPreview, setResolvedPreview] = useState<CoachServiceAssignmentPreview | null>(
    null,
  );
  const [previewState, setPreviewState] = useState<CoachServiceAssignmentPreviewState>("IDLE");
  const [previewClock, setPreviewClock] = useState(() => Date.now());
  const [previewRefreshNonce, setPreviewRefreshNonce] = useState(0);
  const [recoveryView, setRecoveryViewState] = useState<CoachServiceAssignmentRecoveryView>(() =>
    readCoachServiceAssignmentRecovery(context),
  );
  const readingRef = useRef<string | null>(null);
  const previewRequestSequenceRef = useRef("");

  const setRecoveryView = useCallback(
    (view: CoachServiceAssignmentRecoveryView): void => {
      setRecoveryViewState(view);
      onRecoveryViewChange?.(view);
    },
    [onRecoveryViewChange],
  );

  useEffect(() => {
    if (!rotationEnabled) {
      setAssignmentType("REQUESTED");
      setCompleted(null);
    }
  }, [rotationEnabled]);

  const assignmentCoachId = assignmentType === "ROTATION" ? rotationCoachId : requestedCoachId;
  const assignmentCoach = assignableCoaches.find((coach) => coach.id === assignmentCoachId);
  const assignmentRules = serviceOptions.filter((option) => option.coachId === assignmentCoach?.id);
  const selectedTable = activeTables.find((table) => table.sessionId === sessionId);
  const selectedRule = assignmentRules.find((rule) => rule.ruleId === serviceRuleId);
  const customerRequestTable =
    customerRequestQuery.status === "VALID"
      ? activeTables.find((table) => table.sessionId === customerRequestQuery.sessionId)
      : undefined;
  const previewRequest = useMemo<CoachServiceAssignmentPreviewRequest | null>(() => {
    if (!selectedTable?.sessionId || !assignmentCoach || !selectedRule) return null;
    return {
      sessionId: selectedTable.sessionId,
      coachId: assignmentType === "REQUESTED" ? assignmentCoach.id : null,
      expectedCoachId: assignmentCoach.id,
      assignmentType,
      serviceRuleId: selectedRule.ruleId,
      expectedPriceFen: selectedRule.priceFen,
      pricingToken: selectedRule.pricingToken,
    };
  }, [assignmentCoach, assignmentType, selectedRule, selectedTable?.sessionId]);
  const previewSelectionKey = previewRequest
    ? [
        previewRequest.sessionId,
        previewRequest.coachId ?? "ROTATION",
        previewRequest.expectedCoachId,
        previewRequest.assignmentType,
        previewRequest.serviceRuleId,
        previewRequest.expectedPriceFen,
        previewRequest.pricingToken,
      ].join("|")
    : "";
  const previewCandidate = resolvedPreview ?? providedPreview;
  const activePreview =
    previewRequest &&
    previewCandidate &&
    assignmentPreviewMatches(previewCandidate, previewRequest, previewClock)
      ? previewCandidate
      : null;
  const prepaidBalanceInsufficient =
    activePreview?.funding.paymentTiming === "PREPAID" &&
    activePreview.funding.balanceOutcome === "INSUFFICIENT_FOR_INITIAL_CHARGE";

  useEffect(() => {
    if (customerRequestQuery.status !== "VALID") return;
    setSessionId(customerRequestTable?.sessionId ?? "");
    setCompleted(null);
    setCompletedCustomerRequest(false);
  }, [customerRequestQuery, customerRequestTable?.sessionId]);

  useEffect(() => {
    if (requestedCoachId && !assignableCoaches.some((coach) => coach.id === requestedCoachId)) {
      setRequestedCoachId("");
      setManagerReason("");
    }
  }, [assignableCoaches, requestedCoachId]);

  useEffect(() => {
    if (!assignmentRules.some((rule) => rule.ruleId === serviceRuleId)) {
      setServiceRuleId(assignmentRules[0]?.ruleId ?? "");
    }
  }, [assignmentRules, serviceRuleId]);

  useEffect(() => {
    const sequence = `${previewSelectionKey}:${previewRefreshNonce}:${Date.now()}:${Math.random()}`;
    previewRequestSequenceRef.current = sequence;
    setResolvedPreview(null);
    setPreviewClock(Date.now());
    if (!previewRequest) {
      setPreviewState("IDLE");
      return;
    }
    const now = Date.now();
    if (providedPreview && assignmentPreviewMatches(providedPreview, previewRequest, now)) {
      setPreviewState("READY");
      return;
    }
    if (!onPreview) {
      setPreviewState("UNAVAILABLE");
      return;
    }
    setPreviewState("LOADING");
    void onPreview(previewRequest).then(
      (nextPreview) => {
        if (previewRequestSequenceRef.current !== sequence) return;
        const receivedAt = Date.now();
        setPreviewClock(receivedAt);
        if (!assignmentPreviewMatches(nextPreview, previewRequest, receivedAt)) {
          setResolvedPreview(null);
          setPreviewState(
            isCoachServiceAssignmentPreview(nextPreview) &&
              Date.parse(nextPreview.expiresAt) <= receivedAt
              ? "EXPIRED"
              : "ERROR",
          );
          return;
        }
        setResolvedPreview(nextPreview);
        setPreviewState("READY");
      },
      () => {
        if (previewRequestSequenceRef.current !== sequence) return;
        setResolvedPreview(null);
        setPreviewState("ERROR");
      },
    );
  }, [onPreview, previewRefreshNonce, previewSelectionKey, providedPreview, previewRequest]);

  useEffect(() => {
    if (!activePreview) return;
    const delay = Date.parse(activePreview.expiresAt) - Date.now();
    if (delay <= 0) {
      setPreviewClock(Date.now());
      setPreviewState("EXPIRED");
      return;
    }
    const timer = window.setTimeout(
      () => {
        setPreviewClock(Date.now());
        setPreviewState("EXPIRED");
      },
      Math.min(delay + 1, 2_147_483_647),
    );
    return () => window.clearTimeout(timer);
  }, [activePreview]);

  const refreshAfterConfirmedAssignment = useCallback(
    async (command: CoachServiceAssignmentRecoveryCommand): Promise<void> => {
      const refreshes: Promise<unknown>[] = [Promise.resolve(onCompleted())];
      if (command.request.customerRequestEventId !== undefined) {
        refreshes.push(
          queryClient.invalidateQueries({ queryKey: ["service-requests"] }),
          queryClient.invalidateQueries({ queryKey: ["service-work-items"] }),
        );
      }
      await Promise.all(refreshes);
    },
    [onCompleted, queryClient],
  );

  const recoverAuthority = useCallback(
    async (recovery: CoachServiceAssignmentRecovery): Promise<void> => {
      if (recovery.state !== "OUTCOME_UNKNOWN" && recovery.state !== "LEGACY_GET_ONLY") return;
      const requestKey = recovery.command.request.idempotencyKey;
      if (readingRef.current === requestKey) return;
      readingRef.current = requestKey;
      setMessage({
        kind: "INFO",
        text:
          recovery.state === "LEGACY_GET_ONLY"
            ? "正在查询升级前保存的原分配结果。只会查询，不会重发旧请求；确认前不能再次分配、上下钟或换人。"
            : "正在用原请求号查询门店结果。确认前不能再次分配、上下钟或换人。",
      });
      try {
        const result = await getCoachServiceAssignmentByRequest(recovery.command);
        const resolution = reconcileCoachServiceAssignmentAuthority(recovery, context, result);
        setRecoveryView(readCoachServiceAssignmentRecovery(context));
        if (resolution.status === "EXECUTED") {
          setCompleted(resolution.result);
          onConfirmed?.(resolution.result, recovery.command.request.sessionId);
          setCompletedCustomerRequest(
            recovery.command.request.customerRequestEventId !== undefined,
          );
          setMessage({
            kind: "INFO",
            text:
              recovery.command.request.customerRequestEventId === undefined
                ? "门店已确认原分配成功，助教已经上钟并开始计费；系统已通知助教，不会重复分配。"
                : "门店已确认原分配成功，助教已经上钟并开始计费；系统已通知助教，原顾客呼叫助教请求已完成。",
          });
          await refreshAfterConfirmedAssignment(recovery.command);
        } else if (resolution.status === "RETRY_ALLOWED") {
          setMessage({
            kind: "INFO",
            text: "门店已明确原分配没有执行。只能使用已保存的原球桌、助教、项目和请求号重试。",
          });
        } else if (resolution.status === "LEGACY_NOT_FOUND_REQUIRES_NEW_REQUEST") {
          setResolvedPreview(null);
          setPreviewState("IDLE");
          setPreviewRefreshNonce((value) => value + 1);
          setMessage({
            kind: "INFO",
            text: "门店没有找到升级前的原分配结果，旧请求已安全解除。请重新核对球桌、助教、项目、余额和计费后果后发起新请求。",
          });
        } else {
          setMessage({
            kind: "ERROR",
            text: "门店结果无法与原球桌、助教和项目对应。助教经营操作继续暂停，请由店长核对。",
          });
        }
      } catch (error) {
        if (coachServiceAssignmentLegacyResultRequiresStart(error)) {
          if (!clearCoachServiceAssignmentRecovery(recovery, context)) {
            setMessage({
              kind: "ERROR",
              text: "历史分配已经完成，但浏览器无法清除保护记录。助教经营操作继续暂停，请勿重试分配。",
            });
            return;
          }
          setRecoveryView(readCoachServiceAssignmentRecovery(context));
          setMessage({
            kind: "INFO",
            text: "该历史任务已分配，请在当前任务确认上钟。",
          });
          await onCompleted();
          return;
        }
        if (!coachServiceAssignmentOutcomeIsUnknown(error)) {
          if (!clearCoachServiceAssignmentRecovery(recovery, context)) {
            setMessage({
              kind: "ERROR",
              text: "门店已明确没有执行分配，但浏览器无法清除保护记录。助教经营操作继续暂停，请勿重试。",
            });
            return;
          }
          setRecoveryView(readCoachServiceAssignmentRecovery(context));
          setMessage({ kind: "ERROR", text: coachServiceAssignmentFailureMessage(error) });
          await onCompleted();
          return;
        }
        setMessage({
          kind: "ERROR",
          text: `${coachServiceAssignmentFailureMessage(error)} 相关球桌和助教继续锁定，请勿重新提交。`,
        });
      } finally {
        readingRef.current = null;
      }
    },
    [context, onCompleted, onConfirmed, refreshAfterConfirmedAssignment, setRecoveryView],
  );

  useEffect(() => {
    const current = readCoachServiceAssignmentRecovery(context);
    setRecoveryView(current);
    if (
      current.status === "OWNED" &&
      (current.recovery.state === "OUTCOME_UNKNOWN" || current.recovery.state === "LEGACY_GET_ONLY")
    ) {
      void recoverAuthority(current.recovery);
    } else if (current.status === "CONTEXT_MISMATCH") {
      setMessage({
        kind: "ERROR",
        text: "本机保存着另一名员工的助教分配原请求。当前账号不能查看或重试，请切回原账号核对。",
      });
    } else if (current.status === "STORAGE_UNAVAILABLE") {
      setMessage({
        kind: "ERROR",
        text: "浏览器无法安全保存或读取分配保护记录。为防止重复上钟和计费，助教经营操作已暂停。",
      });
    }
  }, [context, recoverAuthority, setRecoveryView]);

  const dispatch = async (recovery: CoachServiceAssignmentRecovery): Promise<void> => {
    if (recovery.state === "LEGACY_GET_ONLY") {
      setMessage({
        kind: "INFO",
        text: "升级前保存的原请求只能查询结果，不能重新发送。系统正在继续查询门店结果。",
      });
      await recoverAuthority(recovery);
      return;
    }
    if (!beginCoachServiceAssignmentDispatch(recovery, context)) {
      setMessage({ kind: "ERROR", text: "原分配请求当前不能安全提交，页面没有发出新请求。" });
      return;
    }
    setPending(true);
    setMessage({ kind: "INFO", text: "正在分配并立即上钟，请勿重复点击。" });
    try {
      const result = await assignCoachService(recovery.command);
      if (!clearCoachServiceAssignmentRecovery(recovery, context)) {
        setMessage({
          kind: "ERROR",
          text: "门店已返回成功，但浏览器无法清除保护记录。助教经营操作继续暂停，请勿重试。",
        });
        return;
      }
      setRecoveryView(readCoachServiceAssignmentRecovery(context));
      setCompleted(result);
      onConfirmed?.(result, recovery.command.request.sessionId);
      setCompletedCustomerRequest(recovery.command.request.customerRequestEventId !== undefined);
      setMessage({
        kind: "INFO",
        text:
          recovery.command.request.customerRequestEventId === undefined
            ? "助教已分配并立即上钟，现在已经开始计时和计费；系统已通知助教。"
            : "助教已分配并立即上钟，现在已经开始计时和计费；系统已通知助教，原顾客呼叫助教请求已完成。",
      });
      setResolvedPreview(null);
      setPreviewState("IDLE");
      setRequestedCoachId("");
      await refreshAfterConfirmedAssignment(recovery.command);
    } catch (error) {
      if (coachServiceAssignmentOutcomeIsUnknown(error)) {
        markCoachServiceAssignmentOutcomeUnknown(recovery, context);
        const current = readCoachServiceAssignmentRecovery(context);
        setRecoveryView(current);
        setMessage({
          kind: "INFO",
          text: "分配结果未知。系统会先查询原请求，结果明确前不会允许再次分配或改动服务。",
        });
        if (current.status === "OWNED") await recoverAuthority(current.recovery);
      } else {
        if (!clearCoachServiceAssignmentRecovery(recovery, context)) {
          setRecoveryView(readCoachServiceAssignmentRecovery(context));
          setMessage({
            kind: "ERROR",
            text: "门店已明确没有执行分配，但浏览器无法清除保护记录。助教经营操作继续暂停，请勿重试。",
          });
          return;
        }
        setRecoveryView(readCoachServiceAssignmentRecovery(context));
        setMessage({ kind: "ERROR", text: coachServiceAssignmentFailureMessage(error) });
        setResolvedPreview(null);
        setPreviewState("IDLE");
        setPreviewRefreshNonce((value) => value + 1);
        await onCompleted();
      }
    } finally {
      finishCoachServiceAssignmentDispatch(recovery, context);
      setPending(false);
    }
  };

  const submit = (): void => {
    if (!selectedTable?.sessionId) {
      setMessage({ kind: "ERROR", text: "请选择一张当前正在使用的球桌。" });
      return;
    }
    if (
      customerRequestQuery.status === "INVALID" ||
      (customerRequestQuery.status === "VALID" &&
        selectedTable.sessionId !== customerRequestQuery.sessionId)
    ) {
      setMessage({
        kind: "ERROR",
        text: "顾客呼叫与当前台单无法对应，系统没有发出分配请求。请返回员工工作中心重新进入。",
      });
      return;
    }
    if (!assignmentCoach) {
      setMessage({ kind: "ERROR", text: "请选择一名当前已签到且可接单的助教。" });
      return;
    }
    if (!selectedRule) {
      setMessage({ kind: "ERROR", text: "请选择这名助教当前可用的服务项目和价格。" });
      return;
    }
    if (!previewRequest || !activePreview) {
      setMessage({
        kind: "ERROR",
        text: "需先读取这桌的余额和计费后果；当前预检尚未完成或已经过期，请重新读取后再确认。",
      });
      return;
    }
    if (prepaidBalanceInsufficient) {
      setMessage({
        kind: "ERROR",
        text: "会员可用余额不足以承担首次预计服务费，系统没有分配助教。请先充值或更改台单结算方式。",
      });
      return;
    }
    const normalizedManagerReason = managerReason.trim();
    if (
      managerReasonRequired &&
      ([...normalizedManagerReason].length < 2 || [...normalizedManagerReason].length > 500)
    ) {
      setMessage({
        kind: "ERROR",
        text: "店长代办派钟必须填写 2 至 500 个字，说明代办原因和现场核对结果。",
      });
      return;
    }
    const command: CoachServiceAssignmentCommand = {
      context,
      request: {
        sessionId: selectedTable.sessionId,
        coachId: assignmentType === "REQUESTED" ? assignmentCoach.id : null,
        expectedCoachId: assignmentCoach.id,
        assignmentType,
        serviceRuleId: selectedRule.ruleId,
        expectedPriceFen: selectedRule.priceFen,
        pricingToken: selectedRule.pricingToken,
        previewToken: activePreview.previewToken,
        ...(managerReasonRequired ? { reason: normalizedManagerReason } : {}),
        ...(customerRequestQuery.status === "VALID"
          ? { customerRequestEventId: customerRequestQuery.customerRequestEventId }
          : {}),
        idempotencyKey: nextAssignmentKey(),
      },
      expected: {
        coachId: assignmentCoach.id,
        coachDisplayName: assignmentCoach.displayName,
        tableNumber: selectedTable.tableNumber,
        serviceTypeName: selectedRule.serviceTypeName,
        priceFen: selectedRule.priceFen,
      },
    };
    const claimed = claimCoachServiceAssignmentRecovery(context, command);
    setRecoveryView(claimed);
    if (claimed.status !== "OWNED") {
      setMessage({
        kind: "ERROR",
        text: "无法安全保存这次分配的完整原请求，系统没有执行。请先处理页面显示的待确认请求。",
      });
      return;
    }
    void dispatch(claimed.recovery);
  };

  const locked = recoveryView.status !== "NONE";
  const customerRequestBindingReady =
    customerRequestQuery.status === "NONE" ||
    (customerRequestQuery.status === "VALID" &&
      selectedTable?.sessionId === customerRequestQuery.sessionId);
  const assignmentReady = Boolean(
    selectedTable &&
      assignmentCoach &&
      selectedRule &&
      activePreview &&
      !prepaidBalanceInsufficient &&
      customerRequestBindingReady &&
      (!managerReasonRequired ||
        ([...managerReason.trim()].length >= 2 && [...managerReason.trim()].length <= 500)),
  );
  const formBlocked = blocked || customerRequestQuery.status === "INVALID";

  return (
    <section className="coach-panel coach-assignment-panel" aria-label="助教分配并立即上钟">
      <div className="coach-section-title">
        <div>
          <span>收银端 · 分配即上钟</span>
          <h2>发起轮钟 / 点钟</h2>
        </div>
        <UsersRound size={21} aria-hidden="true" />
      </div>
      {customerRequestQuery.status === "VALID" ? (
        <div className="coach-form-note">
          <strong>来自顾客的呼叫助教请求</strong>
          <p>请为这桌安排助教并立即上钟。分配成功后，原顾客请求会同时完成。</p>
        </div>
      ) : customerRequestQuery.status === "INVALID" ? (
        <div className="error-band" role="alert">
          顾客呼叫信息不完整或已被改动，系统已停止分配。请返回员工工作中心重新进入。
        </div>
      ) : null}
      <label>
        当前台单
        <select
          value={sessionId}
          disabled={pending || locked || formBlocked || customerRequestQuery.status === "VALID"}
          onChange={(event) => {
            setSessionId(event.target.value);
            setCompleted(null);
          }}
        >
          <option value="">请选择已开台球桌</option>
          {(customerRequestQuery.status === "VALID"
            ? activeTables.filter((table) => table.sessionId === customerRequestQuery.sessionId)
            : activeTables
          ).map((table) => (
            <option value={table.sessionId ?? ""} key={table.tableId}>
              {table.tableNumber} 号桌 · {table.area}
            </option>
          ))}
        </select>
      </label>
      <div className="coach-form-note coach-cross-business-date-note">
        <strong>持续营业台单可以继续分配助教</strong>
        <p>
          即使这桌跨营业日或跨月仍在使用，原台单营业日也不会改变；本次助教服务归入实际当班营业日，费用和提成按真实服务时间计算。营业时间异常长只会提醒，不会自动关台或禁止分配。
        </p>
      </div>
      <fieldset className="coach-choice-row">
        <legend className="visually-hidden">分配方式</legend>
        <button
          className={assignmentType === "ROTATION" ? "is-active" : ""}
          type="button"
          disabled={!rotationEnabled || pending || locked || formBlocked}
          onClick={() => {
            setAssignmentType("ROTATION");
            setCompleted(null);
          }}
        >
          轮钟
        </button>
        <button
          className={assignmentType === "REQUESTED" ? "is-active" : ""}
          type="button"
          disabled={pending || locked || formBlocked}
          onClick={() => {
            setAssignmentType("REQUESTED");
            setCompleted(null);
          }}
        >
          顾客点钟
        </button>
      </fieldset>
      {!rotationEnabled ? (
        <p className="disabled-reason">
          本店未开启助教轮钟，系统不会按排队顺序派钟；顾客点钟仍可正常选择已签到且可接单的助教。
        </p>
      ) : null}
      <p className="coach-assignment-explanation">
        {assignmentType === "ROTATION"
          ? "轮钟：按当前排队顺序选择第一位可接单助教，适合顾客没有指定人选。"
          : "顾客点钟：由顾客指定一名已签到且可接单的助教。"}
      </p>
      {assignmentType === "REQUESTED" ? (
        <label>
          指定助教
          <select
            value={requestedCoachId}
            disabled={pending || locked || formBlocked}
            onChange={(event) => {
              setRequestedCoachId(event.target.value);
              setCompleted(null);
            }}
          >
            <option value="">请选择已签到且可接单的助教</option>
            {assignableCoaches.map((profile) => (
              <option value={profile.id} key={profile.id}>
                {profile.coachNumber} · {profile.displayName}
              </option>
            ))}
          </select>
          <small>只显示已签到、未暂停且没有其他进行中服务的助教。</small>
        </label>
      ) : null}
      <label>
        服务项目与实际价格
        <select
          value={serviceRuleId}
          disabled={pending || locked || formBlocked || serviceOptionsState !== "READY"}
          onChange={(event) => {
            setServiceRuleId(event.target.value);
            setCompleted(null);
          }}
        >
          <option value="">请选择服务项目</option>
          {assignmentRules.map((rule) => (
            <option value={rule.ruleId} key={`${rule.coachId}-${rule.ruleId}-${rule.pricingToken}`}>
              {rule.serviceTypeName} · {money(rule.priceFen)}
            </option>
          ))}
        </select>
        <small>这里显示系统已按所选助教解析好的项目和实际价格；确认时会锁定这次价格。</small>
      </label>

      <fieldset className="coach-assignment-preview">
        <legend>确认前请核对</legend>
        <dl>
          <div>
            <dt>球桌</dt>
            <dd>{selectedTable ? `${selectedTable.tableNumber} 号桌` : "尚未选择"}</dd>
          </div>
          <div>
            <dt>助教</dt>
            <dd>{assignmentCoach?.displayName ?? "尚未选择"}</dd>
          </div>
          <div>
            <dt>服务项目</dt>
            <dd>{selectedRule?.serviceTypeName ?? "尚未选择"}</dd>
          </div>
          <div>
            <dt>实际价格</dt>
            <dd>{selectedRule ? money(selectedRule.priceFen) : "尚未确认"}</dd>
          </div>
          <div>
            <dt>结算方式</dt>
            <dd>
              {activePreview
                ? activePreview.funding.paymentTiming === "PREPAID"
                  ? "会员预付"
                  : "后付（服务结束后结算）"
                : "需先读取"}
            </dd>
          </div>
          <div>
            <dt>首次预计服务费</dt>
            <dd>
              {activePreview
                ? money(activePreview.funding.estimatedInitialServiceFeeFen)
                : "需先读取"}
            </dd>
          </div>
          {activePreview?.funding.paymentTiming === "PREPAID" ? (
            <>
              <div>
                <dt>会员</dt>
                <dd>
                  {activePreview.funding.memberDisplayName}（会员号{" "}
                  {activePreview.funding.memberNumber}）
                </dd>
              </div>
              <div>
                <dt>权威可用余额</dt>
                <dd>{money(activePreview.funding.availableBalanceFen ?? 0)}</dd>
              </div>
              <div>
                <dt>预计首次计费后余额</dt>
                <dd>{money(activePreview.funding.estimatedBalanceAfterInitialFeeFen ?? 0)}</dd>
              </div>
            </>
          ) : null}
        </dl>
        {previewState === "LOADING" ? (
          <output>正在读取这桌的结算方式、权威余额和首次计费后果…</output>
        ) : previewState === "UNAVAILABLE" ? (
          <p className="disabled-reason" role="alert">
            需先读取余额和计费后果；当前页面尚未接入权威预检，不能直接分配助教。
          </p>
        ) : previewState === "EXPIRED" ? (
          <div className="disabled-reason" role="alert">
            <p>刚才的余额和计费后果已经过期，不能继续使用。请重新读取后再确认。</p>
            {onPreview ? (
              <button
                className="button secondary"
                type="button"
                disabled={pending || locked || formBlocked}
                onClick={() => setPreviewRefreshNonce((value) => value + 1)}
              >
                重新读取余额和计费后果
              </button>
            ) : null}
          </div>
        ) : previewState === "ERROR" ? (
          <div className="disabled-reason" role="alert">
            <p>余额或计费后果暂时无法安全确认，系统已停止分配。请重新读取后再试。</p>
            {onPreview ? (
              <button
                className="button secondary"
                type="button"
                disabled={pending || locked || formBlocked}
                onClick={() => setPreviewRefreshNonce((value) => value + 1)}
              >
                重新读取余额和计费后果
              </button>
            ) : null}
          </div>
        ) : previewState === "IDLE" ? (
          <p>选定球桌、助教和服务项目后，系统会先读取余额和首次计费后果。</p>
        ) : null}
        {activePreview?.funding.paymentTiming === "POSTPAID" ? (
          <p>这桌按后付结算。确认后费用进入当前台单，结束服务时按实际服务时长统一结算。</p>
        ) : activePreview?.funding.paymentTiming === "PREPAID" ? (
          <p>首次预计服务费用于判断能否开始服务；最终费用仍按实际服务时长和门店规则结算。</p>
        ) : null}
        {prepaidBalanceInsufficient ? (
          <p className="error-band" role="alert">
            会员可用余额不足，不能确认上钟。请先充值或更改台单结算方式，系统不会透支会员余额。
          </p>
        ) : null}
        <p>
          分配后立即开始计时，助教无需确认；计费同步开始。成功后系统会通知助教，并在助教的“我的通知”中形成正式通知。
        </p>
      </fieldset>

      {managerReasonRequired ? (
        <label>
          店长代办原因和现场核对结果
          <textarea
            name="coach-assignment-manager-reason"
            value={managerReason}
            maxLength={500}
            disabled={pending || locked || formBlocked}
            placeholder="例如：晚班收银员临时离岗，已核对球桌、助教和服务价格"
            onChange={(event) => setManagerReason(event.target.value)}
          />
          <small>填写 2 至 500 个字。确认后助教会立即上钟并开始计时，店长和原因会永久留痕。</small>
        </label>
      ) : null}

      <button
        className="button primary"
        type="button"
        disabled={pending || locked || formBlocked || !assignmentReady}
        onClick={submit}
      >
        <UserPlus size={17} aria-hidden="true" /> 确认分配并立即上钟
      </button>

      {tableReadFailed ? (
        <p className="disabled-reason">
          球台状态暂时无法安全确认，系统已停止分配助教，请刷新后重试。
        </p>
      ) : activeTables.length === 0 ? (
        <p className="disabled-reason">请先在“球台”开台；助教服务必须关联真实台单。</p>
      ) : customerRequestQuery.status === "VALID" && !customerRequestTable ? (
        <p className="disabled-reason">
          原顾客呼叫对应的台单已结束或更换，不能改选其他球桌。请返回员工工作中心核对。
        </p>
      ) : null}
      {assignmentType === "ROTATION" && rotationAssignmentEmptyReason ? (
        <p className="disabled-reason">{rotationAssignmentEmptyReason}</p>
      ) : null}
      {assignmentType === "REQUESTED" && requestedAssignmentEmptyReason ? (
        <p className="disabled-reason">{requestedAssignmentEmptyReason}</p>
      ) : null}
      {serviceOptionsState === "LOADING" ? (
        <p className="disabled-reason">正在读取可分配的服务项目和实际价格…</p>
      ) : null}
      {serviceOptionsState === "ERROR" ? (
        <p className="disabled-reason">
          服务项目和实际价格暂时无法安全确认，系统已停止分配，请刷新后重试。
        </p>
      ) : null}
      {serviceOptionsState === "EMPTY" ? (
        <p className="disabled-reason">当前门店还没有已启用的助教服务项目和价格。</p>
      ) : null}
      {serviceOptionsState === "READY" && assignmentCoach && assignmentRules.length === 0 ? (
        <p className="disabled-reason">
          {assignmentCoach.displayName} 的级别没有已启用的服务项目和价格。
        </p>
      ) : null}

      {recoveryView.status === "OWNED" ? (
        <div className="coach-assignment-recovery" role="alert">
          <strong>原分配请求待确认</strong>
          <span>原球桌、助教、项目、价格和请求号已锁定，不能改成另一笔。</span>
          {recoveryView.canRetry ? (
            <button
              className="button secondary"
              type="button"
              disabled={pending}
              onClick={() => void dispatch(recoveryView.recovery)}
            >
              使用原请求安全重试
            </button>
          ) : null}
        </div>
      ) : recoveryView.status !== "NONE" ? (
        <div className="coach-assignment-recovery" role="alert">
          当前账号或浏览器不能安全处理已保存的原分配请求，助教经营操作已暂停。
        </div>
      ) : null}

      {message ? (
        <output className={message.kind === "ERROR" ? "error-band" : "coach-form-note"}>
          <Clock3 size={17} aria-hidden="true" />
          <span>{message.text}</span>
        </output>
      ) : null}

      {completed ? (
        <output className="coach-assignment-result" aria-live="polite">
          <div>
            <Check size={18} aria-hidden="true" />
            <strong>分配成功，助教已上钟</strong>
          </div>
          <p>
            {completed.tableNumber} 号桌 · {completed.serviceTypeName} ·{" "}
            {completed.assignmentType === "ROTATION" ? "轮钟" : "顾客点钟"} · 开始时间{" "}
            {dateTime(completed.startedAt)}
          </p>
          <p>系统已通知助教，服务已经开始计费，无需等待助教再次确认。</p>
          {completedCustomerRequest ? (
            <p>原顾客呼叫助教请求已完成，不需要再到员工工作中心手动点完成。</p>
          ) : null}
        </output>
      ) : null}
    </section>
  );
}
